import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { Sun, Clapperboard, Mountain, Activity, Leaf, BadgeIndianRupee } from "lucide-react";

const SECTORS = [
  { key: "solar", icon: Sun, to: "/solar", img: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { key: "media", icon: Clapperboard, to: "/media", img: "https://images.unsplash.com/photo-1632187981988-40f3cbaeef5e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { key: "mining", icon: Mountain, to: "/mining", img: "https://images.unsplash.com/photo-1622645916455-aa13b87438ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
];

export default function HeroShowcase() {
  const { t } = useApp();
  const [active, setActive] = useState(SECTORS[0]);
  const info = t.showcase.sectors[active.key];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      data-testid="hero-showcase"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-teal-300/15 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative rounded-2xl overflow-hidden border border-amber-400/30 bg-[#014845]/70 backdrop-blur-xl shadow-2xl shadow-teal-950/40">
        <div className="relative h-56 sm:h-64 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img
              key={active.key}
              src={active.img}
              alt={info.label}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-[#014845] via-[#014845]/30 to-transparent pointer-events-none" />

          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#014845]/80 backdrop-blur border border-teal-400/40 text-[11px] font-bold text-teal-100" data-testid="hero-showcase-status">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> {info.status}
          </div>
          <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/90 text-[#014845] text-[11px] font-extrabold shadow-lg" data-testid="hero-showcase-badge-portfolio">
            <BadgeIndianRupee className="w-3.5 h-3.5" /> {t.showcase.portfolio}
          </div>

          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2">
            <div>
              <div className="text-white font-extrabold text-lg leading-tight">{info.label}</div>
              <div className="text-amber-300 text-sm font-bold" data-testid="hero-showcase-metric">{info.metric}</div>
            </div>
            <Link to={active.to} data-testid="hero-showcase-explore"
              className="shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold bg-white/15 border border-white/30 text-white backdrop-blur hover:bg-amber-400 hover:text-[#014845] hover:border-amber-300 transition-all">
              {t.common.explore} →
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-2 p-3">
          {SECTORS.map((s) => {
            const Icon = s.icon;
            const on = active.key === s.key;
            return (
              <button key={s.key} onClick={() => setActive(s)} data-testid={`hero-sector-tab-${s.key}`}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-bold border transition-all ${on ? "bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845] border-amber-300 shadow-md" : "bg-white/10 text-teal-100 border-white/20 hover:bg-white/20"}`}>
                <Icon className="w-3.5 h-3.5" /> {t.showcase.sectors[s.key].label.split(" ")[0]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="absolute -bottom-4 -left-3 sm:-left-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 border border-teal-200 shadow-xl text-[11px] font-extrabold text-[#017471]" data-testid="hero-showcase-badge-carbon">
        <Leaf className="w-3.5 h-3.5" /> {t.showcase.carbon}
      </div>
      <div className="absolute -top-4 right-6 hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 border border-teal-200 shadow-xl text-[11px] font-extrabold text-[#017471]" data-testid="hero-showcase-badge-live">
        <Activity className="w-3.5 h-3.5" /> {t.showcase.verticals}
      </div>
    </motion.div>
  );
}
